/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';

import { ColorName, Colors } from '../../../theme';

import type { LiteralUnion } from '../../../types';

import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
extend([a11yPlugin]);

export const Styles = labelStyles('Tag', {
  root: (backgroundColor: string, textColor: string) => {
    return css`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: var(--zds-space-0) var(--zds-space-4);
      text-align: center;
      border-radius: var(--zds-radius-small);
      background-color: ${backgroundColor};
      color: ${textColor};

      &[data-size='x-small'] {
        height: var(--zds-size-20);
        font: var(--zds-minimal-print-2);
      }

      &[data-size='small'] {
        height: var(--zds-size-24);
        font: var(--zds-body-2);
      }

      &[data-variant='default'] {
        background-color: var(--zds-ui-primary-weaker);
        color: var(--zds-text-default);
      }

      &[data-variant='success'] {
        background-color: var(--zds-status-success-weaker);
        color: var(--zds-text-default);
      }

      &[data-variant='error'] {
        background-color: var(--zds-status-error-weaker);
        color: var(--zds-text-default);
      }

      &[data-variant='warning'] {
        background-color: var(--zds-status-warning-weaker);
        color: var(--zds-text-default);
      }

      &[data-variant='inverted'] {
        background-color: var(--zds-gray-warm-10);
        color: var(--zds-text-inverted);
      }

      &[data-variant='simple'] {
        background-color: transparent;
        color: var(--zds-text-weaker);
        border: 1px solid var(--zds-stroke-default);
      }
    `;
  },
});

const tagVariants = [
  'default',
  'success',
  'error',
  'warning',
  'inverted',
  'simple',
] as const;

type TagVariantName = (typeof tagVariants)[number];

export type Props = {
  /**
   * Content to render inside the label. Generally this is plain text,
   * but other content such as icons or Avatars may also be rendered.
   */
  children: React.ReactNode;
  /**
   * Optional property for overriding styles with CSS-in-JS or defining a custom class on the top-level Select element.
   * Use with caution as the DOM structure of the component may change.
   */
  className?: string;
  /**
   * The size of `Tag`.
   * @default 'small'
   */
  size?: 'x-small' | 'small';
  /**
   * The color variant of the label. There are six standard variants but any design system color can be used.
   * @default 'default'
   * */
  variant?: LiteralUnion<TagVariantName, ColorName>;
};

/**
 * Renders a tag component used for displaying text tags.
 */
export const Tag = ({
  children,
  className,
  size = 'small',
  variant = 'default',
}: Props) => {
  let backgroundColor: ColorName = 'UiPrimaryWeaker';
  let textColor: ColorName = 'TextDefault';

  // If the provided color option was a predefined variant...
  if (tagVariants.find((tagVariant) => tagVariant === variant)) {
    switch (variant) {
      case 'default':
        backgroundColor = 'UiPrimaryWeaker';
        textColor = 'TextDefault';
        break;
      case 'success':
        backgroundColor = 'StatusSuccessWeaker';
        textColor = 'TextDefault';
        break;
      case 'error':
        backgroundColor = 'StatusErrorWeakest';
        textColor = 'TextDefault';
        break;
      case 'warning':
        backgroundColor = 'StatusWarningWeakest';
        textColor = 'TextDefault';
        break;
      case 'inverted':
        backgroundColor = 'GrayWarm10';
        textColor = 'TextInverted';
        break;
      case 'simple':
        backgroundColor = 'transparent';
        textColor = 'TextWeaker';
        break;
    }
  }
  // If the provided color option is a custom color, calculate the correct text color.
  else {
    backgroundColor = variant as ColorName;

    const lightTextContrast = colord(Colors[backgroundColor]).contrast(
      Colors.TextInverted
    );
    const darkTextContrast = colord(Colors[backgroundColor]).contrast(
      Colors.TextStronger
    );

    if (lightTextContrast > darkTextContrast) {
      textColor = 'TextInverted';
    } else {
      textColor = 'TextStronger';
    }
  }

  return (
    <span
      className={className}
      css={Styles.root(Colors[backgroundColor], Colors[textColor])}
      data-size={size}
      data-variant={variant}
      data-zds
    >
      {children}
    </span>
  );
};
